<script lang="ts">
export default {
  name: 'DocumentDeleteConfirmModal',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import { documentService, type ExtractionStatus } from '@/services/v2/DocumentService';
import { useToast } from '@/composables/useToast';

type ModalState = 'loading-status' | 'confirming' | 'deleting' | 'error-status';

const props = defineProps<{
  show: boolean;
  documentId: string | null;
  accountId: number | null;
  fileName?: string | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'deleted', payload: { documentId: string; policyId?: number }): void;
}>();

const toast = useToast();
const state = ref<ModalState>('loading-status');
const status = ref<ExtractionStatus | null>(null);
const statusError = ref<string | null>(null);
let fetchRequestId = 0;

const warningCopy =
  'Warning: Information from this document has been validated by a human and is used throughout the Platform. Deleting this document will also remove this data.';

const displayName = computed(() => props.fileName ?? 'this document');

const hasValidatedExtraction = computed(() => status.value?.hasValidatedExtraction === true);

const actionDisabled = computed(() => state.value !== 'confirming');

const loading = computed(() => state.value === 'deleting');

async function fetchStatus() {
  if (!props.documentId) {
    return;
  }
  const requestId = ++fetchRequestId;
  state.value = 'loading-status';
  statusError.value = null;
  status.value = null;
  try {
    const result = await documentService.getExtractionStatus(props.accountId, props.documentId);
    if (requestId !== fetchRequestId) {
      return;
    }
    status.value = result;
    state.value = 'confirming';
  } catch (err) {
    if (requestId !== fetchRequestId) {
      return;
    }
    console.error('Failed to fetch extraction status', err);
    statusError.value = 'Could not check extraction status. Try again.';
    state.value = 'error-status';
  }
}

async function handleConfirm() {
  if (!props.documentId || state.value !== 'confirming') {
    return;
  }
  state.value = 'deleting';
  try {
    if (props.accountId !== null) {
      await documentService.deleteDocumentByIdAndAccount(props.accountId, props.documentId);
    } else {
      await documentService.deleteDocumentById(props.documentId);
    }
    toast.success('Document successfully deleted');
    emit('deleted', {
      documentId: props.documentId,
      policyId: status.value?.policyId,
    });
    emit('close');
  } catch (err: unknown) {
    const axiosErr = err as { response?: { status?: number } };
    if (axiosErr?.response?.status === 404) {
      emit('deleted', {
        documentId: props.documentId,
        policyId: status.value?.policyId,
      });
      emit('close');
      return;
    }
    console.error('Failed to delete document', err);
    toast.error('Document failed to delete');
    state.value = 'confirming';
  }
}

function handleCancel() {
  if (state.value === 'deleting') {
    return;
  }
  emit('close');
}

watch(
  () => props.show,
  (next) => {
    if (next && props.documentId) {
      fetchStatus();
    } else if (!next) {
      state.value = 'loading-status';
      status.value = null;
      statusError.value = null;
    }
  },
  { immediate: true },
);
</script>

<template>
  <AppModal
    :show="show"
    title="Delete Document"
    action-button-label="Delete"
    :handle-cancel="handleCancel"
    :handle-action="handleConfirm"
    :loading="loading"
    :action-disabled="actionDisabled"
    size="lg">
    <template #content>
      <div
        v-if="state === 'loading-status'"
        class="flex items-center gap-3 py-4">
        <span class="loading loading-sm loading-spinner"></span>
        <span>Checking extraction status…</span>
      </div>

      <div
        v-else-if="state === 'error-status'"
        class="py-2">
        <div class="text-error mb-3">{{ statusError }}</div>
        <button
          class="btn btn-sm"
          type="button"
          @click="fetchStatus">
          Retry
        </button>
      </div>

      <div
        v-else
        class="flex flex-col gap-3">
        <div
          v-if="hasValidatedExtraction"
          class="alert alert-warning"
          data-testid="validated-extraction-warning">
          {{ warningCopy }}
        </div>

        <p class="text-sm">Are you sure you want to delete the following file?</p>
        <div class="bg-base-200 rounded-md px-3 py-2 font-mono text-sm break-all">
          {{ displayName }}
        </div>
        <p class="text-base-content/60 text-xs">This cannot be undone.</p>
      </div>
    </template>
  </AppModal>
</template>
